import { FormattedMessage, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';
import { Button, Modal, Tag, Table } from 'antd';
import { DeleteButton } from '@/components/_common/Buttons';
import CreateQuotationSupplierModal from '@/components/Movements/Quotations/QuotationSuppliers/CreateQuotationSupplierModal';
import { useQuotationContext } from '..';
import { ColumnsType } from 'antd/lib/table';
import {
  QuotationSupplier,
  QuotationSupplierStatus,
  QuotationSuppliersLabel,
} from '@/core/domain/stock/Quotations/Quotations.types';
import {
  useDeleteQuotationSupplier,
  useQuotationSuppliers,
} from '@/core/domain/stock/Quotations/Quotations.hooks';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { useDisclosure } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';

const QuotationDetailsSuppliersPage = () => {
  const intl = useIntl();
  const { quotationId } = useRouteMatch<{ quotationId: string }>().params;
  const quotation = useQuotationContext();

  const {
    data: quotationSuppliersResult,
    refetch: quotationSuppliersRefetch,
    isLoading,
  } = useQuotationSuppliers(Number(quotationId));

  const quotationSupplierRemove = useDeleteQuotationSupplier(
    Number(quotationId),
  );

  const [modalVisible, modalActions] = useDisclosure(false, {
    onClose: () => quotationSuppliersRefetch,
  });

  const handleRemoveQuotationSupplier = (
    quotationSupplier: QuotationSupplier,
  ) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'quotations.details.suppliers.remove' }),
      content: `${intl.formatMessage({
        id: 'quotations.details.suppliers.remove.alert',
      })} ${quotationSupplier?.name}`,
      onOk: async () => {
        await quotationSupplierRemove.mutateAsync(quotationSupplier.supplierId);

        quotationSuppliersRefetch();
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<QuotationSupplier> = [
    {
      key: 'name',
      dataIndex: 'name',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.details.suppliers.name',
      }),
    },
    {
      key: 'email',
      dataIndex: 'email',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.details.suppliers.email',
      }),
    },
    {
      key: 'type',
      dataIndex: 'type',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.details.suppliers.type',
      }),
    },
    {
      key: 'status',
      dataIndex: 'status',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.details.suppliers.status',
      }),
      render: (value: QuotationSupplierStatus) => (
        <Tag color="processing">
          <FormattedMessage
            id={`quotations.${QuotationSuppliersLabel[value]}`}
          />
        </Tag>
      ),
    },
  ];

  if (!quotation?.disabled) {
    columns.push({
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: 100,
      fixed: 'right',
      align: 'right',
      render: (row) => (
        <DeleteButton onClick={() => handleRemoveQuotationSupplier(row)}>
          <FormattedMessage id="commons.list.action.button.remove" />
        </DeleteButton>
      ),
    });
  }

  return (
    <>
      <div>
        <div className="create-page-title">
          <SpaceForButton justifyContent="space-between">
            <h2>
              <FormattedMessage id="quotations.details.suppliers" />
            </h2>
            <div>
              <Button
                block
                type="primary"
                onClick={modalActions.open}
                disabled={quotation?.disabled}
              >
                <FormattedMessage id="quotations.details.suppliers.create" />
              </Button>
            </div>
          </SpaceForButton>
        </div>

        <Table<QuotationSupplier>
          loading={isLoading}
          rowKey={(row) => row.supplierId}
          dataSource={quotationSuppliersResult?.result?.data}
          columns={columns}
          scroll={{ x: getTableScrollX(columns) }}
          pagination={false}
        />
      </div>

      <CreateQuotationSupplierModal
        visible={modalVisible}
        handleVisible={modalActions.toggle}
        currentAddedQuotationSuppliers={
          quotationSuppliersResult?.result?.data || []
        }
      />
    </>
  );
};

export default QuotationDetailsSuppliersPage;
